import { UploadFile } from "antd";
import { useFetchAppCategories } from "@lhd/hooks";
import { useEffect, useMemo, useState } from "react";
import {
  BUTTON_HEIGHT,
  FONT_SIZE,
  FONT_SIZE_VALUE,
  getFileNameFromURL,
} from "@lhd/constants";
import { AppPicture } from "types/app.type";
import { generateUuid } from "@lhd/features/Apps/utils";
import { imageNames } from "@lhd/features/AppCategories/AppCategoriesControl/constant";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { useSelector } from "react-redux";
import { DISPLAY_SIZES } from "@lhd/types";

export type ReceivedProps = Record<string, any>;
const imagePaths = imageNames.map(
  (imageItem) => `/images/icon-app-category/${imageItem}`,
);

const useAppFormInformation = (props: ReceivedProps) => {
  const [imageUrl, setImageUrl] = useState<any>();
  const [thumbnailList, setThumbnailList] = useState<UploadFile[]>([]);
  const [typeUpload, setTypeUpload] = useState<"icon" | "file">("icon");
  const [selectedImage, setSelectedImage] = useState<string>(imagePaths?.[0]);
  const [isOpenModalSelectIcon, setIsOpenModalSelectIcon] =
    useState<boolean>(false);

  const { data: dataAppCategories } = useFetchAppCategories();

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const formik = props.form;
  const { setFieldValue, values, touched, errors } = formik;

  const isNameRequiredError = touched.name && errors.name === "必須項目です";

  const categoriesApp = (dataAppCategories?.data || [])
    .filter((categoryItem) => categoryItem.status === "enabled")
    .map((categoryItem) => {
      return {
        value: categoryItem?.id,
        label: categoryItem?.name,
      };
    });

  const handleUrl = (file: any) => {
    if (!file) return null;

    if (file?.name && !file.url) {
      return URL.createObjectURL(file);
    }

    return file?.url;
  };

  const handleUploadThumbnail = (_: any, fileList: UploadFile[]) => {
    const newList = [...thumbnailList, ...fileList];
    setThumbnailList(newList);
    setFieldValue("app_pictures", newList);
    return false;
  };

  const handleDeleteThumbnail = (uid: string) => {
    const newList = [...thumbnailList].filter((file) => file.uid !== uid);
    setThumbnailList(newList);
    setFieldValue("app_pictures", newList);
  };

  const handleDeletePicture = () => {
    setFieldValue("picture", null);
    setImageUrl(null);
  };

  const handleChangeTypeUpload = async (type: "icon" | "file") => {
    setTypeUpload(type);

    if (type === "file") {
      if (!imageUrl && values?.picture?.url) {
        // remove icon
        setFieldValue("picture", null);
      }
    } else {
      const response = await fetch(selectedImage);
      const blobFile = await response.blob();
      const file = new File([blobFile], imagePaths?.[0], {
        type: "image/png",
      });

      // set icon
      setFieldValue("picture", file);
    }
  };

  const handleOpenModalSelectIcon = () => setIsOpenModalSelectIcon(true);

  const handleCloseModalSelectIcon = () => setIsOpenModalSelectIcon(false);

  const imageName = useMemo(() => {
    let result = "";
    if (!imageUrl) return "";

    if (typeof imageUrl === "string") {
      result = getFileNameFromURL(imageUrl);
    } else {
      result = imageUrl?.name;
    }

    return result?.length > 16 ? result.slice(0, 16) + "..." : result;
  }, [imageUrl]);

  const handleOkModalSelect = async (pathItem: string) => {
    setFieldValue("selectedImage", pathItem);
    setSelectedImage(pathItem);
    const selectedImageName = getFileNameFromURL(pathItem);

    if (
      typeUpload === "icon" &&
      selectedImage &&
      selectedImageName &&
      selectedImageName !== getFileNameFromURL(values?.picture?.url || "")
    ) {
      const response = await fetch(pathItem);
      const blobFile = await response.blob();
      const file = new File([blobFile], selectedImageName, {
        type: "image/png",
      });

      // set icon
      setFieldValue("picture", file);
    } else if (typeUpload === "file" && !imageUrl && values?.picture?.url) {
      // remove icon
      setFieldValue("picture", null);
    } else if (typeUpload === "file" && typeof imageUrl !== "string") {
      // set image
      setFieldValue("picture", imageUrl);
    }

    handleCloseModalSelectIcon();
  };

  useEffect(() => {
    if (values?.picture?.url && typeof values?.picture?.url === "string") {
      const iconOriginalName = getFileNameFromURL(values?.picture?.url);
      const indexPathIconOriginalNameConst = imageNames.findIndex(
        (nameItem) => nameItem === iconOriginalName,
      );

      if (indexPathIconOriginalNameConst > -1) {
        setSelectedImage(imagePaths?.[indexPathIconOriginalNameConst]);
        setTypeUpload("icon");
      } else {
        setImageUrl(values?.picture?.url);
        setTypeUpload("file");
      }
    } else if (values?.id && !values?.picture) {
      setTypeUpload("file");
    }
  }, [values?.id, values?.picture]);

  useEffect(() => {
    if (
      values.app_pictures &&
      values.app_pictures?.length > 0 &&
      thumbnailList.length === 0
    ) {
      const listAppPictures = values?.app_pictures?.map(
        (pictureItem: AppPicture) =>
          pictureItem?.picture_original_url
            ? {
                name: getFileNameFromURL(pictureItem?.picture_original_url),
                url: pictureItem?.picture_original_url,
                uid: generateUuid(),
              }
            : pictureItem,
      );
      setThumbnailList(listAppPictures);
    }
  }, [thumbnailList, values.app_pictures]);

  return {
    imageUrl,
    setImageUrl,
    handleUrl,
    thumbnailList,
    handleUploadThumbnail,
    handleDeleteThumbnail,
    categoriesApp,
    handleDeletePicture,
    typeUpload,
    handleChangeTypeUpload,
    handleCloseModalSelectIcon,
    handleOpenModalSelectIcon,
    setSelectedImage,
    selectedImage,
    imageName,
    isOpenModalSelectIcon,
    imagePaths,
    handleOkModalSelect,
    displaySize,
    height,
    size,
    isNameRequiredError,
    ...props,
  };
};

export type Props = ReturnType<typeof useAppFormInformation>;

export default useAppFormInformation;
